
import Vue from 'vue';
import { mapActions, mapState, mapGetters } from 'vuex';
import _isNil from 'lodash/isNil';
import { MyBalanceMixin, TeamUserMixin } from '@/mixins';
import {
	getRunnitNodeRunPrice,
	RunnitNode,
	RunnitNodeStaticFields,
	RunnitNodeStaticFieldsKey,
	RUNNIT_NODE_DEF_PRICING_TYPE,
	RUNNIT_NODE_STATIC_FIELDS_KEY,
	RUNNIT_NODE_DEF_TOOL_APP_TYPE,
	RUNNIT_NODE_DEF_TAG_TYPE,
	RUNNIT_NODE_FIELD_TYPE,
	RunnitNodeDefTag,
	RunnitNodeDef,
	RunnitNodeField,
	RunnitNodeFieldGroup,
} from '@run-diffusion/shared';
import RunnitTokens from '@/views/Runnits/RunnitTokens.vue';
import ComplexBackground from '@/components/designElements/ComplexBackground.vue';
import NumberWithIncrementer from '@/components/base/NumberWithIncrementer.vue';
import AutoSaveRunnitNodeRunContainer from '@/views/Runnits/RunnitSettings/AutoSaveRunnitNodeRunContainer.vue';
import RunnitDynamicField from '@/views/Runnits/RunnitSettings/RunnitDynamicField.vue';
import RunnitStaticFieldsContainer from '@/views/Runnits/RunnitSettings/static/RunnitStaticFieldsContainer.vue';
import { RunnitBulkActionsMixin } from '@/mixins/RunnitBulkActionsMixin';
import { StoreLoadingState } from '@/store';
import BaseButton from '@/components/base/BaseButton.vue';
import RunRunnitButton from './RunRunnitButton.vue';
import { TRAINING_QUALITY_CARDS } from "@/views/Runnits/constants";
import { ROUTER } from '@/router/constants';
import BaseDrawerOrBottomSheet from '@/components/base/BaseDrawerOrBottomSheet.vue';
import { SNACKBAR_STATUS } from '@/constants/constants';
import GlassButton from '@/components/base/GlassButton.vue';
import RunnitImage from '@/views/Runnits/RunnitImage.vue';
import CooldownProgressMenu from '@/views/Runnits/CooldownProgressMenu.vue';
import { collectAutoSetHiddenFields, filterRenderableAdvancedFields } from '@/utils/runnitBatchVariableFields';

export default Vue.extend({
	name: 'RunnitSettingsDrawer',
	mixins: [
		MyBalanceMixin,
		RunnitBulkActionsMixin,
		TeamUserMixin,
	],
	props: {
		isAnyNodeSelected: { type: Boolean, default: false },
		selectedNode: { type: Object, default: null },
		actionsOnly: { type: Boolean, default: false },
		isSingleTool: { type: Boolean, default: false },
		isTemplate: { type: Boolean, default: false },
	},
	data () {
		return {
			RUNNIT_NODE_STATIC_FIELDS_KEY,
			RUNNIT_NODE_DEF_TOOL_APP_TYPE,

			incrementAutoSaveTrigger: 0,
			bottomOfScrollReached: false,
			advancedExpanded: null as number | null,

			// FORM
			formValid: true,

			runnitButtonDisabled: {
				disabled: false,
				reasons: [],
			},
			promptFieldLoading: false,
		};
	},
	computed: {
		...mapState([
			'user',
			'team',
			'publicRunnitNodeDefTagsMap',
			'loadingDraftRunnitNodeRun',
			'draftRunnitNodeRun',
			'runnitState',
		]),
		...mapGetters([
			'isMobileNavOpen',
			'runnitNodesLimitMap',
		]),
		/**
		 * Can the user switch which tool this panel is running?
		 *
		 * Only on a single tool: a board switches tools a different way, so its
		 * panel keeps a plain title with no dropdown affordance. Trainers are
		 * excluded, as they were when this was an icon button.
		 */
		canSwitchTool () {
			return !!(
				this.isSingleTool &&
				this.selectedNode &&
				this.selectedNode.nodeDef &&
				this.selectedNode.nodeDef.appType !== RUNNIT_NODE_DEF_TOOL_APP_TYPE.TRAINER
			);
		},
		inputValues () {
			return this.runnitState.inputValues
		},
		staticInputValues () {
			return this.runnitState.staticInputValues
		},
		isOverPlanNodesLimit () {
			return !!(
				this.selectedNode &&
				!this.runnitNodesLimitMap[this.selectedNode.id]
			);
		},
		isToolUnpublished () {
			return !!(this.selectedNode &&
				typeof this.selectedNode.nodeDef !== 'string' && // Nested object is fetched
				(!this.selectedNode.nodeDef.isPublished || !this.selectedNode.nodeDef.publishedAt) &&
				!this.user.isAdmin);
		},
		isToolBlockedForTeamUser () {
			if (!this.selectedNode || typeof this.selectedNode.nodeDef === 'string') return false;
			return this.isNodeDefBlockedForTeamUser(this.selectedNode.nodeDef);
		},
		disableActions () {
			// Only disable actions while the draft binding is still resolving.
			// Requiring `draftRunnitNodeRun` to exist can keep actions disabled when switching tools quickly
			// (draft creation is async and may lag behind the binding resolving).
			return this.loadingDraftRunnitNodeRun || this.loadingDraftRunnitNodeRun === null;
		},
		showBatchWizardEntry (): boolean {
			if (this.isTemplate) return false;
			const n = this.selectedNode;
			return !!(
				n &&
				typeof n.nodeDef !== 'string' &&
				n.nodeDef.appType === RUNNIT_NODE_DEF_TOOL_APP_TYPE.RUNNIT &&
				n.nodeDef.batchable &&
				this.hasBatchGenerationPermission
			);
		},
		isBatchWizardEntryDisabled (): boolean {
			return !!this.runnitButtonDisabled?.disabled;
		},
		determinedStaticFields () {
			if (!this.selectedNode) return {};
			return this._get(this.selectedNode, 'nodeDef.staticFields') || {};
		},
		numResultsEffectiveMax (): number {
			const numResultsField: RunnitNodeStaticFields['numResults'] = this.determinedStaticFields[RUNNIT_NODE_STATIC_FIELDS_KEY.numResults];
			const toolMax: number = numResultsField?.max ?? 1;
			if (!numResultsField) return 1;
			if (this.hasNumResultsLimitPermission && this.numResultsLimitMax) {
				return Math.min(toolMax, this.numResultsLimitMax);
			}
			return toolMax;
		},
		showNumResultsInput (): boolean {
			const numResultsField: RunnitNodeStaticFields['numResults'] = this.determinedStaticFields[RUNNIT_NODE_STATIC_FIELDS_KEY.numResults];
			if (!numResultsField) return false;
			const toolMin: number = numResultsField.min ?? 1;
			return toolMin < this.numResultsEffectiveMax;
		},
		numResultsStaticInputValue (): number {
			const numResultsField: RunnitNodeStaticFields['numResults'] = this.determinedStaticFields[RUNNIT_NODE_STATIC_FIELDS_KEY.numResults];
			const toolMin: number = numResultsField?.min ?? 1;
			const fromStaticInput: number = this.staticInputValues[RUNNIT_NODE_STATIC_FIELDS_KEY.numResults];
			const val: number = fromStaticInput > 0 ? fromStaticInput : toolMin;
			if (!this.showNumResultsInput) return toolMin;
			if (this.hasNumResultsLimitPermission && this.numResultsLimitMax) {
				return Math.min(this.numResultsEffectiveMax, Math.max(toolMin, val));
			}
			return Math.max(toolMin, val);
		},
		totalRunCost () {
			const nodeDef: RunnitNodeDef = this.selectedNode?.nodeDef;
			if (
				!nodeDef ||
				typeof nodeDef === 'string' ||
				nodeDef.pricingType !== RUNNIT_NODE_DEF_PRICING_TYPE.COST ||
				nodeDef.costPerResult <= 0
			) {
				return null;
			}
			const determinedFields = nodeDef.fields || [];
			return getRunnitNodeRunPrice(
				nodeDef.costPerResult,
				this.numResultsStaticInputValue,
				nodeDef.costPerResultLogics,
				this.inputValues,
				determinedFields
			);
		},
		estimatedTrainTime () {
			const tags: RunnitNodeDefTag[] = Object.keys(this.selectedNode.nodeDef.tags).map((tagId: string) => this.publicRunnitNodeDefTagsMap[tagId]);
			const qualityTag = tags.find((tag: RunnitNodeDefTag) => tag.type === RUNNIT_NODE_DEF_TAG_TYPE.TRAINING_QUALITY);
			if (qualityTag) {
				const qualityCard = TRAINING_QUALITY_CARDS[qualityTag.id];
				return qualityCard?.timeEstimate || '';
			} else {
				return '';
			}
		},
	},
	watch: {
		selectedNode: {
			immediate: true,
			handler (newVal: RunnitNode, oldVal: RunnitNode) {
				const newId: string = this._get(newVal, 'id') || null;
				const oldId: string = this._get(oldVal, 'id') || null;
				if (newId && newId !== oldId) {
					this.bindDraftRunnitNodeRun({ userId: this.user.id, nodeId: newId });
					this.advancedExpanded = null;
				}
			},
		},
		loadingDraftRunnitNodeRun: {
			immediate: true,
			handler (newVal: StoreLoadingState, oldVal: StoreLoadingState) {
				if (oldVal && !newVal && !this.draftRunnitNodeRun) {
					// create the initial draft with the default values
					this.incrementAutoSaveTrigger++;
				}
			}
		},
	},
	methods: {
		...mapActions([
			'updateSnackbar',
			'bindDraftRunnitNodeRun',
			'updateRunnitState',
			'updateModelState',
		]),
		_isNil,
		collectAutoSetHiddenFields,
		filterRenderableAdvancedFieldsForDrawer (fields: (RunnitNodeField | RunnitNodeFieldGroup)[]): (RunnitNodeField | RunnitNodeFieldGroup)[] {
			return filterRenderableAdvancedFields(fields, this.inputValues || {}, !!this.user?.isAdmin);
		},
		setSettingsDrawerOpen (value: boolean) {
			this.$emit('set-settings-drawer-open', value);
			if (value) {
				this.setBulkActionMenuOpen(false);
			}
		},
		openCooldownMenu () {
			this.$nextTick(() => {
				const drawer = this.$refs.cooldownProgressMenu;
				const actionsOnly = this.$refs.cooldownProgressMenuActionsOnly;
				if (drawer && typeof drawer.openMenu === 'function') drawer.openMenu();
				else if (actionsOnly && typeof actionsOnly.openMenu === 'function') actionsOnly.openMenu();
			});
		},
		onInputValuesInput (newVal: Record<string, any>) {
			this.updateRunnitState({
				inputValues: {
					...this.inputValues,
					...newVal,
				},
			});
		},
		onStaticInputValuesInputChange (newVal: Record<string, any>) {
			this.updateRunnitState({
				staticInputValues: {
					...this.staticInputValues,
					...newVal,
				},
			});
		},
		onStaticInputValuesChange (key: RunnitNodeStaticFieldsKey, val: any) {
			this.onStaticInputValuesInputChange({ [key]: val });

			this.$nextTick(() => {
				// on next tick, so that the state set above has a chance to render before triggering auto save
				this.incrementAutoSaveTrigger++;
			});
		},
		handleScrollReachingBottom () {
			const scrollContainer = this.$refs.scrollContainer;

			this.bottomOfScrollReached = scrollContainer.scrollTop > 240;
		},
		setToolsLibraryDialogOpenState (open: boolean) {
			// Always open the unified RunnitToolsLibraryDialog for change-tool
			// — see the matching comment in PrimaryForm.vue for context. The
			// legacy isKit branch routed to KitDialogSelect, which had no kit
			// switcher and trapped users in a single kit.
			this.updateRunnitState({ toolsLibraryDialogOpen: open });
		},
		goToRunnitHomePage () {
			this.routerReplace(this.$route, this.$router, { name: ROUTER.RUNNITS_HOME });
		},
		removeTool () {
			this.$emit('on-remove-tool', this.selectedNode);
		},
		onDrawerRunRunnitButtonClick () {
			this.$emit('run-clicked');
			if (this.$vuetify.breakpoint.smAndDown) {
				this.setSettingsDrawerOpen(false);
			}
		},
		resetForm () {
			this.updateRunnitState({
				inputValues: {},
				staticInputValues: {},
			});

			// Use emitNodeRunDefaults to reset to defaults
			if (this.$refs.autoSaveRunnitNodeRunContainer) {
				this.$refs.autoSaveRunnitNodeRunContainer.emitNodeRunDefaults(null, this.selectedNode);
			}


			// Trigger auto save with empty values
			this.incrementAutoSaveTrigger++;

			// Show success message
			this.updateSnackbar({
				status: SNACKBAR_STATUS.SUCCESS,
				message: 'Form reset to defaults',
				show: true,
			});
		},
		onPromptLoadingChanged (isLoading: boolean) {
			this.promptFieldLoading = isLoading;
		},
		onCreateBoardClick () {
			this.$emit('create-board');
		},
	},
	components: {
		BaseDrawerOrBottomSheet,
		RunnitTokens,
		ComplexBackground,
		NumberWithIncrementer,
		AutoSaveRunnitNodeRunContainer,
		RunnitDynamicField,
		RunnitStaticFieldsContainer,
		BaseButton,
		RunRunnitButton,
		GlassButton,
		RunnitImage,
		CooldownProgressMenu,
	},
});
